import { useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, jsonBody } from "../../api/client";
import type { Contact } from "../../api/types";
import { Card, EditorPage, EmptyState, ErrorPanel, Field, FormActions } from "../../ui/components";
import { DetailPageSkeleton } from "../../ui/skeletons";
import { useContact } from "./queries";

export function ContactEditorPage() {
  const { contactId } = useParams<{ contactId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const contactQuery = useContact(contactId);
  const contact = contactId ? contactQuery.data : undefined;
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<unknown>();
  // Type governs whether the company fields mean anything, and the server clears them for an
  // individual. Tracking it here lets the form say so while it is being filled in, rather than
  // accepting a company name and discarding it after the save.
  const [type, setType] = useState<string | null>(null);
  const [consent, setConsent] = useState<boolean | null>(null);
  const backTo = contactId ? `/contacts/${contactId}` : "/contacts";
  const backLabel = contactId ? "Back to contact" : "Back to contacts";
  // Null means the field has not been touched, so the stored value still governs. Anything
  // other than an explicit grant counts as no consent.
  const chosenType = type ?? contact?.type ?? "organisation";
  const actsForItself = chosenType === "individual";
  const consentChecked = consent ?? contact?.marketingEmail.state === "granted";

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const marketingEmail = data.get("marketingEmail") === "on";
    const payload = {
      type: String(data.get("type")),
      status: String(data.get("status")),
      displayName: String(data.get("displayName")),
      organisation: actsForItself ? "" : String(data.get("organisation")),
      legalName: actsForItself ? "" : String(data.get("legalName")),
      email: String(data.get("email")),
      phone: String(data.get("phone")),
      roleTitle: String(data.get("roleTitle")),
      billingAddress: {
        line1: String(data.get("line1")),
        city: String(data.get("city")),
        region: String(data.get("region")),
        postalCode: String(data.get("postalCode")),
        country: String(data.get("country")),
      },
      taxReference: String(data.get("taxReference")),
      notes: String(data.get("notes")),
      tags: String(data.get("tags"))
        .split(",")
        .map((tag) => tag.trim())
        .filter(Boolean),
      portalEnabled: data.get("portalEnabled") === "on",
      marketingEmail,
      // Consent without a recorded source is a claim with no evidence, so the server refuses
      // it. Sending the source only when consent is given keeps a stale justification from
      // surviving a withdrawal. Both fields are written to the shared preference record rather
      // than to the contact row.
      marketingSource: marketingEmail ? String(data.get("marketingSource")) : "",
      // The version the form was rendered from. The server rejects the write if the record
      // moved on, rather than letting the later save win by arriving last.
      version: contact?.version ?? 0,
    };
    setWorking(true);
    setError(undefined);
    try {
      const item = await api<Contact>(
        contact ? `/api/v1/contacts/${contact.id}` : "/api/v1/contacts",
        { method: contact ? "PUT" : "POST", body: jsonBody(payload) },
      );
      await queryClient.invalidateQueries({ queryKey: ["contacts"] });
      navigate(`/contacts/${item.id}`);
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  if (contactId && contactQuery.isLoading)
    return (
      <DetailPageSkeleton title="Edit contact" backTo="/contacts" backLabel="Back to contacts" />
    );

  return (
    <EditorPage
      title={contact ? "Edit contact" : "Add contact"}
      description={
        contact?.displayName ??
        "A contact is the person you deal with. Record the organization they act for as well, so whoever calls them knows who they are calling."
      }
      backTo={backTo}
      backLabel={backLabel}
    >
      {contactId && !contact ? (
        <EmptyState
          title="Contact not found"
          description="The contact is outside your current access scope or no longer exists."
        />
      ) : (
        <Card>
          {error ? <ErrorPanel error={error} /> : null}
          <form className="form-grid" onSubmit={save} key={contact?.id ?? "new-contact"}>
            <Field
              label="Contact type"
              hint="An individual acts for nobody but themselves, so the organization fields are put away."
            >
              <select
                name="type"
                value={chosenType}
                onChange={(event) => setType(event.target.value)}
              >
                <option value="organisation">Organization</option>
                <option value="individual">Individual</option>
                <option value="government">Government</option>
                <option value="nonprofit">Nonprofit</option>
              </select>
            </Field>
            <Field
              label="Contact status"
              hint="Archived contacts stay searchable and keep their history, but drop out of the default list and are not offered as marketing recipients."
            >
              <select name="status" defaultValue={contact?.status ?? "active"}>
                <option value="active">Active</option>
                <option value="archived">Archived</option>
              </select>
            </Field>
            <Field label="Full name">
              <input
                name="displayName"
                required
                maxLength={200}
                defaultValue={contact?.displayName}
              />
            </Field>
            <Field label="Role or title">
              <input name="roleTitle" maxLength={100} defaultValue={contact?.roleTitle} />
            </Field>
            <Field
              label="Organization"
              hint={
                actsForItself
                  ? "Not recorded for an individual."
                  : "Who this person acts for. This is the name that appears on their bookings and invoices."
              }
            >
              <input
                name="organisation"
                maxLength={200}
                disabled={actsForItself}
                defaultValue={contact?.organisation}
              />
            </Field>
            <Field label="Legal name" hint="Only if it differs from the organization name.">
              <input
                name="legalName"
                maxLength={200}
                disabled={actsForItself}
                defaultValue={contact?.legalName}
              />
            </Field>
            <Field
              label="Email address"
              hint="Required once marketing consent is recorded, because there is nowhere to send to without it."
            >
              <input
                name="email"
                type="email"
                required={consentChecked}
                defaultValue={contact?.email}
              />
            </Field>
            <Field label="Phone number">
              <input name="phone" maxLength={50} defaultValue={contact?.phone} />
            </Field>
            <Field label="Tax reference">
              <input name="taxReference" maxLength={100} defaultValue={contact?.taxReference} />
            </Field>
            <Field label="Address">
              <input name="line1" defaultValue={String(contact?.billingAddress?.line1 ?? "")} />
            </Field>
            <Field label="City">
              <input name="city" defaultValue={String(contact?.billingAddress?.city ?? "")} />
            </Field>
            <Field label="State or region">
              <input name="region" defaultValue={String(contact?.billingAddress?.region ?? "")} />
            </Field>
            <Field label="Postal code">
              <input
                name="postalCode"
                defaultValue={String(contact?.billingAddress?.postalCode ?? "")}
              />
            </Field>
            <Field label="Country">
              <input
                name="country"
                defaultValue={String(contact?.billingAddress?.country ?? "US")}
              />
            </Field>
            <Field label="Tags" hint="Comma separated">
              <input name="tags" defaultValue={contact?.tags.join(", ")} />
            </Field>
            <Field label="Notes">
              <textarea name="notes" defaultValue={contact?.notes} />
            </Field>
            <label className="check-field">
              <input name="portalEnabled" type="checkbox" defaultChecked={contact?.portalEnabled} />{" "}
              Enable customer portal access
            </label>
            <label className="check-field">
              <input
                name="marketingEmail"
                type="checkbox"
                checked={consentChecked}
                onChange={(event) => setConsent(event.target.checked)}
              />{" "}
              This person agreed to receive marketing email
            </label>
            <Field
              label="How consent was given"
              hint="Required before any marketing send: where and how they agreed, for example a signed inquiry form on March 4. This writes the same record the customer portal writes, so the person and the platform cannot end up disagreeing."
            >
              <input
                name="marketingSource"
                maxLength={200}
                required={consentChecked}
                disabled={!consentChecked}
                defaultValue={
                  contact?.marketingEmail.state === "granted" ? contact.marketingEmail.source : ""
                }
              />
            </Field>
            <div className="full">
              <FormActions
                submitting={working}
                submitLabel={contact ? "Save contact" : "Create contact"}
                onCancel={() => navigate(backTo)}
              />
            </div>
          </form>
        </Card>
      )}
    </EditorPage>
  );
}
